import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import AuthService from "services/auth.service";
import useAuth from "hooks/useAuth";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import "react-toastify/dist/ReactToastify.css";
import { toast, ToastContainer } from "react-toastify";

import { Field, Button } from "components/ui";

const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconShield = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const IconArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

// Base URL for raw axios calls (OTP endpoints) — adjust if your AuthService
// already centralizes this somewhere you'd rather reuse.
const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";

const Login = () => {
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  // ── Step control: "credentials" | "otp" ────────────────
  const [step, setStep] = useState("credentials");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── OTP state ───────────────────────────────────────────
  const [otpToken, setOtpToken] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef = useRef(null);

  // Countdown for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Autofocus code input when entering OTP step
  useEffect(() => {
    if (step === "otp" && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  const decodeToken = (token) => {
    try {
      if (!token) return null;
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (_) { return null; }
  };

  // Shared post-login logic (used once OTP is verified and we have a real token)
  const completeLogin = async (accessToken) => {
    localStorage.setItem("accessToken", accessToken);
    toast.success("Welcome back!");

    const decoded = decodeToken(accessToken);
    const roles = decoded?.authorities ?? [];

    let user = null;
    try {
      const userResponse = await axiosPrivate.get("/api/users/me");
      user = userResponse.data;
    } catch (err) {
      console.warn("Could not fetch user profile", err);
    }

    setAuth({
      user,
      roles,
      accessToken,
      tenantId: user?.tenant?.id ?? null,
    });

    if (roles.includes("SYSTEM_ADMIN")) navigate("/admin");
    else if (roles.includes("BRANCH_ADMIN")) navigate("/branch");
    else if (roles.includes("STOCK_MANAGER")) navigate("/stock");
    else if (roles.includes("RH")) navigate("/rh");
    else if (roles.includes("CASHIER")) navigate("/cashier");
    else navigate("/home");
  };

  // ── Step 1: submit email + password ─────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await AuthService.login(email, password);
      const data = response?.data;

      // Backend now returns { otpRequired: true, otpToken } instead of tokens directly
      if (data?.otpRequired) {
        setOtpToken(data.otpToken);
        setStep("otp");
        setResendCooldown(30);
        toast.info("We sent a verification code to your email");
        return;
      }

      // Fallback: if some account skips OTP and tokens come back directly
      const accessToken = data?.access_token || data?.accessToken;
      if (!accessToken) throw new Error("No access token");
      await completeLogin(accessToken);
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error?.response?.data?.message || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step 2: submit OTP code ──────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (code.trim().length < 6) {
      toast.warn("Enter the 6-digit code");
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/verify-otp`, {
        otpToken,
        code: code.trim(),
      }, { withCredentials: true });

      const accessToken = res?.data?.access_token || res?.data?.accessToken;
      if (!accessToken) throw new Error("No access token");
      await completeLogin(accessToken);
    } catch (error) {
      console.error("OTP verification error:", error);
      toast.error(error?.response?.data?.message || "Invalid or expired code");
    } finally {
      setVerifying(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      await axios.post(`${API_BASE}/auth/resend-otp`, { otpToken });
      toast.success("New code sent");
      setResendCooldown(30);
    } catch (error) {
      console.error("Resend OTP error:", error);
      toast.error(error?.response?.data?.message || "Could not resend code");
    } finally {
      setResending(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep("credentials");
    setCode("");
    setOtpToken("");
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.05fr 1fr",
        gap: 0,
        background: "rgba(255,255,255,0.03)",
        backdropFilter: "blur(18px)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 24,
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
        overflow: "hidden",
        minHeight: 580,
      }}
      className="auth-split"
    >
      {/* Left: brand / value-prop */}
      <aside
        style={{
          padding: "56px 48px",
          display: "flex",
          flexDirection: "column",
          gap: 24,
          background:
            "radial-gradient(120% 80% at 0% 0%, rgba(79,70,229,0.45) 0%, transparent 50%), radial-gradient(120% 80% at 100% 100%, rgba(127,119,221,0.35) 0%, transparent 60%)",
          color: "#fff",
        }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 44, height: 44, borderRadius: 12,
              background: "linear-gradient(135deg, #4F46E5, #7F77DD)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, color: "#fff",
              boxShadow: "0 10px 30px rgba(79,70,229,0.55)",
            }}
          >
            S
          </span>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, letterSpacing: "-0.02em" }}>
            STOCK<span style={{ color: "#7F77DD" }}>POS</span>
          </span>
        </div>

        <div style={{ marginTop: 16 }}>
          <h1
            className="t-display-lg"
            style={{ color: "#fff", margin: 0, maxWidth: 460 }}
          >
            Run inventory, point-of-sale, and books in one place.
          </h1>
          <p style={{ color: "rgba(255,255,255,0.72)", fontSize: 15, lineHeight: 1.6, marginTop: 16, maxWidth: 440 }}>
            Sign in to manage your branches, monitor stock movements in real time, and close your books with confidence.
          </p>
        </div>

        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12, marginTop: "auto" }}>
          {[
            "Multi-branch, multi-role access",
            "Live stock tracking & AI-assisted invoicing",
            "Accountant-grade reporting",
          ].map((t) => (
            <li key={t} style={{ display: "flex", alignItems: "center", gap: 12, color: "rgba(255,255,255,0.85)", fontSize: 14 }}>
              <span
                style={{
                  width: 22, height: 22, borderRadius: 999,
                  background: "rgba(79,70,229,0.25)",
                  border: "1px solid rgba(127,119,221,0.5)",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  color: "#C7D2FE",
                  flexShrink: 0,
                }}
              >
                <IconCheck />
              </span>
              {t}
            </li>
          ))}
        </ul>
      </aside>

      {/* Right: form */}
      <section style={{ padding: "56px 48px", background: "#fff", color: "var(--text-body)" }}>
        <div style={{ maxWidth: 380, marginInline: "auto" }}>

          {step === "credentials" ? (
            <>
              <span className="t-body-xs" style={{ color: "var(--brand-primary)" }}>Welcome back</span>
              <h2 className="t-heading-lg" style={{ marginTop: 6, marginBottom: 6 }}>Sign in to StockPos</h2>
              <p className="t-body-sm t-muted" style={{ marginBottom: 28 }}>
                Use your work email and password to access your dashboard.
              </p>

              <form onSubmit={handleSubmit} style={{ display: "grid", gap: 16 }}>
                <Field
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  icon={<IconMail />}
                />
                <Field
                  label="Password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  icon={<IconLock />}
                  hint={
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      style={{ background: "transparent", border: 0, color: "var(--brand-primary)", padding: 0, cursor: "pointer", fontSize: 12, fontWeight: 500 }}
                    >
                      {showPwd ? "Hide password" : "Show password"}
                    </button>
                  }
                />

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13 }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", color: "var(--text-body)" }}>
                    <input type="checkbox" style={{ accentColor: "var(--brand-primary)" }} />
                    Remember me
                  </label>
                  <a href="#" onClick={(e) => e.preventDefault()} style={{ color: "var(--brand-primary)", fontWeight: 500, textDecoration: "none" }}>
                    Forgot password?
                  </a>
                </div>

                <Button type="submit" variant="primary" block loading={submitting}>
                  Sign in
                </Button>
              </form>

              <p style={{ marginTop: 24, fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>
                Don't have an account?{" "}
                <Link to="/auth/register" style={{ color: "var(--brand-primary)", fontWeight: 600, textDecoration: "none" }}>
                  Create one
                </Link>
              </p>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleBackToCredentials}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: "transparent", border: 0, padding: 0, marginBottom: 18,
                  color: "var(--text-muted)", fontSize: 13, cursor: "pointer",
                }}
              >
                <IconArrowLeft /> Back
              </button>

              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: "rgba(79,70,229,0.1)",
                border: "1px solid rgba(79,70,229,0.25)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                color: "var(--brand-primary)", marginBottom: 18,
              }}>
                <IconShield />
              </div>

              <h2 className="t-heading-lg" style={{ marginTop: 0, marginBottom: 6 }}>Verify your identity</h2>
              <p className="t-body-sm t-muted" style={{ marginBottom: 28 }}>
                Enter the 6-digit code we sent to{" "}
                <strong style={{ color: "var(--text-body)" }}>{email}</strong>
              </p>

              <form onSubmit={handleVerifyOtp} style={{ display: "grid", gap: 16 }}>
                <Field
                  ref={codeInputRef}
                  label="Verification code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  style={{ letterSpacing: "0.3em", textAlign: "center", fontSize: 18, fontWeight: 600 }}
                />

                <Button type="submit" variant="primary" block loading={verifying}>
                  Verify &amp; sign in
                </Button>
              </form>

              <p style={{ marginTop: 20, fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>
                Didn't receive a code?{" "}
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || resending}
                  style={{
                    background: "transparent", border: 0, padding: 0,
                    color: resendCooldown > 0 ? "var(--text-muted)" : "var(--brand-primary)",
                    fontWeight: 600, cursor: resendCooldown > 0 ? "default" : "pointer",
                  }}
                >
                  {resending ? "Sending…" : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                </button>
              </p>
            </>
          )}

        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .auth-split { grid-template-columns: 1fr !important; min-height: auto !important; }
          .auth-split > aside { padding: 32px 28px !important; }
          .auth-split > section { padding: 36px 28px !important; }
        }
      `}</style>

      <ToastContainer />
    </div>
  );
};

export default Login;